import { Link, router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { Button } from '../../../components/ui/button'
import { Badge } from '../../../components/ui/badge'
import { Key, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog'

interface CayuProvidedSecret {
  id: number
  service_name: string
  secret_name: string
  projects_count: number
  organizations_count: number
  created_at: string
  updated_at: string
}

interface PagyMetadata {
  page: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface AdminCayuProvidedSecretsIndexProps {
  cayu_provided_secrets: CayuProvidedSecret[]
  pagy: PagyMetadata
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
}

const SERVICE_ICONS: Record<string, string> = {
  openai: '🤖',
  anthropic: '🧠',
  google_gemini: '✨',
  sendgrid: '📧',
}

const SERVICE_NAMES: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google_gemini: 'Google Gemini',
  sendgrid: 'SendGrid',
}

function Index({ cayu_provided_secrets, pagy }: AdminCayuProvidedSecretsIndexProps) {
  const { t } = useTranslation('admin')
  const [keyToDelete, setKeyToDelete] = useState<CayuProvidedSecret | null>(null)

  const handleDelete = () => {
    if (!keyToDelete) return

    if (keyToDelete.projects_count > 0) {
      alert(t('cayuProvidedKeys.delete.inUseError'))
      setKeyToDelete(null)
      return
    }

    router.delete(`/admin/cayu_provided_secrets/${keyToDelete.id}`, {
      onSuccess: () => {
        setKeyToDelete(null)
      },
      onError: (errors) => {
        console.error('Delete error:', errors)
        setKeyToDelete(null)
      }
    })
  }

  // Group keys by service
  const keysByService = cayu_provided_secrets.reduce((acc, key) => {
    if (!acc[key.service_name]) {
      acc[key.service_name] = []
    }
    acc[key.service_name].push(key)
    return acc
  }, {} as Record<string, CayuProvidedSecret[]>)

  return (
    <>
      <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-2 py-2">
            <div className="px-4 lg:px-6">
              {/* Page Header */}
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">{t('cayuProvidedKeys.index.title')}</h1>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t('cayuProvidedKeys.index.description')}
                  </p>
                </div>

                <Link href="/admin/cayu_provided_secrets/new">
                  <Button>
                    {t('cayuProvidedKeys.index.addNew')}
                  </Button>
                </Link>
              </div>

              {/* Keys by Service */}
              <div className="space-y-6">
                {Object.keys(keysByService).length > 0 ? (
                  Object.entries(keysByService).map(([serviceName, keys]) => (
                    <div key={serviceName} className="bg-card rounded-lg border border-border overflow-hidden">
                      <div className="px-6 py-4 border-b border-border bg-muted/30">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{SERVICE_ICONS[serviceName] || '🔑'}</span>
                          <h2 className="text-lg font-semibold text-foreground">
                            {SERVICE_NAMES[serviceName] || serviceName}
                          </h2>
                          <Badge variant="secondary" className="ml-2">
                            {t('cayuProvidedKeys.usage.keyCount', { count: keys.length })}
                          </Badge>
                        </div>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                {t('cayuProvidedKeys.table.keyName')}
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                {t('cayuProvidedKeys.table.usage')}
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                {t('cayuProvidedKeys.table.created')}
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                {t('cayuProvidedKeys.table.actions')}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-card divide-y divide-border">
                            {keys.map((key) => (
                              <tr key={key.id} className="hover:bg-muted/30">
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-2">
                                    <Key className="w-4 h-4 text-purple-600" />
                                    <span className="text-sm font-medium text-foreground">
                                      {key.secret_name}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <span>
                                      {t('cayuProvidedKeys.usage.orgs', { count: key.organizations_count })}
                                    </span>
                                    <span className="text-xs">-</span>
                                    <span>
                                      {t('cayuProvidedKeys.usage.projects', { count: key.projects_count })}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-sm text-muted-foreground">
                                    {new Date(key.created_at).toLocaleDateString()}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center justify-end gap-2">
                                    <Link
                                      href={`/admin/cayu_provided_secrets/${key.id}`}
                                      className="text-sm text-blue-600 hover:text-blue-900 mr-2"
                                    >
                                      {t('cayuProvidedKeys.actions.view')}
                                    </Link>
                                    <Link href={`/admin/cayu_provided_secrets/${key.id}/edit`}>
                                      <Button variant="ghost" size="sm">
                                        <Pencil className="w-4 h-4" />
                                      </Button>
                                    </Link>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setKeyToDelete(key)}
                                      disabled={key.projects_count > 0}
                                      className={key.projects_count > 0 ? 'opacity-50 cursor-not-allowed' : ''}
                                    >
                                      <Trash2 className="w-4 h-4 text-red-600" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="bg-card rounded-lg border border-border p-12 text-center">
                    <Key className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">{t('cayuProvidedKeys.empty.title')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('cayuProvidedKeys.empty.description')}
                    </p>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {pagy.pages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {t('cayuProvidedKeys.pagination.showing', { from: pagy.from, to: pagy.to, count: pagy.count })}
                  </div>
                  <div className="flex gap-2">
                    {pagy.prev && (
                      <Link
                        href={`/admin/cayu_provided_secrets?page=${pagy.prev}`}
                        className="px-3 py-1 text-sm border border-border rounded hover:bg-muted"
                      >
                        {t('cayuProvidedKeys.pagination.previous')}
                      </Link>
                    )}
                    <span className="px-3 py-1 text-sm">
                      {t('cayuProvidedKeys.pagination.pageOf', { page: pagy.page, pages: pagy.pages })}
                    </span>
                    {pagy.next && (
                      <Link
                        href={`/admin/cayu_provided_secrets?page=${pagy.next}`}
                        className="px-3 py-1 text-sm border border-border rounded hover:bg-muted"
                      >
                        {t('cayuProvidedKeys.pagination.next')}
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!keyToDelete} onOpenChange={() => setKeyToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('cayuProvidedKeys.delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              <span dangerouslySetInnerHTML={{ __html: t('cayuProvidedKeys.delete.description', { name: keyToDelete?.secret_name }) }} />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cayuProvidedKeys.delete.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {t('cayuProvidedKeys.delete.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
